import { useState } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { Button } from '@scramble/ui';
import { useIsMobile } from '@scramble/hooks';
import { prettyMoney, safeDecimal } from '@scramble/utils';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import IconBank from '@scramble/design-system/icons/bank.svg';
import { useGetWithdrawInfo } from '@/query/queries/cash/useGetWithdrawInfo';
import { useCreateWithdrawRequest } from '@/query/queries/cash/useCreateWithdrawRequest';
import Analytics from '@/analyticsGA';
import s from './index.module.scss';

const id = MODAL_IDS.review_withdraw_modal;

export const ReviewWithdrawModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ amount: reviewAmount, destination }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { closeModal } = useModal();
  const { setErrors, setFieldValue } = useFormikContext();
  const { mutateAsync: createWithdrawRequest } = useCreateWithdrawRequest();
  const { data } = useGetWithdrawInfo()
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    try {
      setLoading(true);
      Analytics.withdrawWithdrawSubmit();
      const amount = safeDecimal(reviewAmount).toNumber();

      if (amount > data?.cash) {
        setErrors({
          amount: t('modal:review_withdraw_modal.not_enough_money'),
        });
        return;
      }

      const payload = {
        account: destination.id,
        amount,
      };

      const res = await createWithdrawRequest(payload);
      if (res?.success) {
        setFieldValue('amount', '');
        closeModal(id)
      }
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <div className={s.icon}>
          <img src={IconBank} width={28} height={28} alt="bank" />
        </div>
        <div className="f-headline-2xs f-500">
          {t('modal:review_withdraw_modal.title')}
        </div>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          <div className={s.summary}>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_withdraw_modal.withdrawal_amount')}
              </div>
              <div className="f-body-md f-500">
                {prettyMoney(Number(reviewAmount))}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_withdraw_modal.withdrawal_destination')}
              </div>
              <div className="f-body-md f-500">
                {destination.account_number}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_withdraw_modal.processing_time')}
              </div>
              <div className="f-body-md f-500">
                {t('modal:review_withdraw_modal.num_days', { count: '2-4' })}
              </div>
            </div>
            <div className={s.row}>
              <div className="f-body-md">
                {t('modal:review_withdraw_modal.fee')}
              </div>
              <div className="f-body-md f-500">
                {t('modal:review_withdraw_modal.no_fee')}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="tonal"
          size="lg"
          label={t('modal:review_withdraw_modal.cancel')}
          onClick={() => closeModal(id)}
        />
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          disabled={loading}
          onClick={onSubmit}
          label={
            loading
              ? t('modal:review_withdraw_modal.processing')
              : t('modal:review_withdraw_modal.confirm')
          }
        />
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};